import { useState } from "react";
import toast from "react-hot-toast";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  useCreateRoom,
  useDeleteRoom,
  useRooms,
  useUpdateRoom,
} from "../api/use-rooms";
import type { Room, RoomCreateRequest, RoomUpdateRequest } from "../types";
import { RoomFormDialog } from "./room-form-dialog";

interface EditRoomsDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
}

type FormState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; room: Room };

export function EditRoomsDialog({
  open,
  onClose,
  scheduleId,
}: EditRoomsDialogProps) {
  // Only fetch when the dialog is actually open.
  const roomsQuery = useRooms(scheduleId, { enabled: open });
  const createRoom = useCreateRoom(scheduleId);
  const updateRoom = useUpdateRoom(scheduleId);
  const deleteRoom = useDeleteRoom(scheduleId);

  const [form, setForm] = useState<FormState>({ kind: "closed" });
  const [formError, setFormError] = useState<string | null>(null);
  /** Id of the row whose inline delete mutation is currently in flight. */
  const [pendingRoomId, setPendingRoomId] = useState<string | null>(null);

  const handleAdd = () => {
    setFormError(null);
    setForm({ kind: "create" });
  };

  const handleEdit = (room: Room) => {
    setFormError(null);
    setForm({ kind: "edit", room });
  };

  const handleCreateSubmit = async (data: RoomCreateRequest) => {
    setFormError(null);
    try {
      await createRoom.mutateAsync(data);
      setForm({ kind: "closed" });
      toast.success("Room created");
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to create room",
      );
    }
  };

  const handleUpdateSubmit = async (data: RoomUpdateRequest) => {
    if (form.kind !== "edit") return;
    setFormError(null);
    try {
      const original = form.room;
      const fieldsChanged =
        data.name !== original.name ||
        data.type !== original.type ||
        (data.capacity ?? null) !== (original.capacity ?? null) ||
        (data.notes ?? null) !== (original.notes ?? null);

      if (fieldsChanged) {
        await updateRoom.mutateAsync({ roomId: original.id, payload: data });
      }
      setForm({ kind: "closed" });
      toast.success("Room updated");
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to update room",
      );
    }
  };

  const handleDelete = async (room: Room) => {
    const confirmed = window.confirm(
      `Delete room "${room.name}"? This cannot be undone.`,
    );
    if (!confirmed) return;
    setPendingRoomId(room.id);
    try {
      await deleteRoom.mutateAsync(room.id);
      toast.success("Room deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete room");
    } finally {
      setPendingRoomId((current) => (current === room.id ? null : current));
    }
  };

  const rooms = roomsQuery.data ?? [];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Edit rooms"
        description="Internal rooms must have a capacity. External rooms are exempt."
        maxWidthClassName="max-w-2xl"
        testId="edit-rooms-dialog"
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleAdd}
              data-testid="add-room-button"
            >
              Add room
            </Button>
          </div>

          {roomsQuery.isLoading && (
            <div className="py-8 flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {roomsQuery.isError && (
            <div
              role="alert"
              data-testid="rooms-load-error"
              className="px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs"
            >
              {roomsQuery.error instanceof Error
                ? roomsQuery.error.message
                : "Failed to load rooms."}
            </div>
          )}

          {!roomsQuery.isLoading &&
            !roomsQuery.isError &&
            rooms.length === 0 && (
              <div
                data-testid="rooms-empty"
                className="text-sm text-text-muted py-6 text-center"
              >
                No rooms yet. Click “Add room” to create one.
              </div>
            )}

          {!roomsQuery.isLoading && !roomsQuery.isError && rooms.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-subtle text-left text-xs text-text-muted uppercase tracking-wide">
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">Capacity</th>
                    <th className="py-2 pl-3 font-medium text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rooms.map((room) => (
                    <RoomRow
                      key={room.id}
                      room={room}
                      isPending={pendingRoomId === room.id}
                      onEdit={() => handleEdit(room)}
                      onDelete={() => handleDelete(room)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              data-testid="close-button"
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>

      {form.kind === "create" && (
        <RoomFormDialog
          open
          mode="create"
          onClose={() => setForm({ kind: "closed" })}
          onCreate={handleCreateSubmit}
          isLoading={createRoom.isPending}
          error={formError}
        />
      )}

      {form.kind === "edit" && (
        <RoomFormDialog
          // Re-mount per room id so local form state re-seeds when the user
          // opens edit on a different row without closing the dialog first.
          key={form.room.id}
          open
          mode="edit"
          room={form.room}
          freshRoom={rooms.find((r) => r.id === form.room.id) ?? form.room}
          onClose={() => setForm({ kind: "closed" })}
          onUpdate={handleUpdateSubmit}
          isLoading={updateRoom.isPending}
          error={formError}
        />
      )}
    </>
  );
}

interface RoomRowProps {
  room: Room;
  /** True while this row's delete (or other future per-row mutation) is in flight. */
  isPending: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const ROW_ACTION_BTN_BASE =
  "text-text-faint text-sm rounded px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-50 disabled:cursor-not-allowed";

function RoomRow({ room, isPending, onEdit, onDelete }: RoomRowProps) {
  const needsCapacity = room.type === "Internal" && room.capacity === null;
  return (
    <tr
      data-testid={`room-row-${room.id}`}
      aria-busy={isPending || undefined}
      className="border-b border-border-subtle/40"
    >
      <td className="py-2.5 pr-3 font-medium text-white">{room.name}</td>
      <td className="py-2.5 pr-3 text-text-soft">{room.type}</td>
      <td className="py-2.5 pr-3 text-text-soft">
        {needsCapacity ? (
          <span
            data-testid={`needs-capacity-${room.id}`}
            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-300"
          >
            needs capacity
          </span>
        ) : (
          (room.capacity ?? "—")
        )}
      </td>
      <td className="py-2.5 pl-3 text-right">
        <div className="inline-flex gap-3">
          <button
            type="button"
            onClick={onEdit}
            disabled={isPending}
            data-testid={`edit-room-${room.id}`}
            aria-label={`Edit room ${room.name}`}
            className={`${ROW_ACTION_BTN_BASE} hover:text-text-soft`}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={isPending}
            data-testid={`delete-room-${room.id}`}
            aria-label={`Delete room ${room.name}`}
            className={`${ROW_ACTION_BTN_BASE} hover:text-red-400`}
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}
